import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Code, Key, Plus, Trash2, Loader2, Copy, ExternalLink } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

const DeveloperPortal = () => {
  const [carregando, setCarregando] = useState(true);
  const [keys, setKeys] = useState([]);
  const [nomeNovaKey, setNomeNovaKey] = useState("");
  const [gerando, setGerando] = useState(false);
  const [chaveGerada, setChaveGerada] = useState(null);
  const [docs, setDocs] = useState(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const [respKeys, respDocs] = await Promise.all([
        authService.fetchAuth(`${API}/keys`),
        fetch(`${API}/docs`),
      ]);
      if (respKeys.ok) setKeys(await respKeys.json());
      if (respDocs.ok) setDocs(await respDocs.json());
    } catch (error) {
      console.error("Erro ao carregar developer portal:", error);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const gerarChave = async () => {
    if (!nomeNovaKey) {
      toast.error("Dê um nome para identificar esta chave.");
      return;
    }
    setGerando(true);
    try {
      const resp = await authService.fetchAuth(`${API}/keys/generate?nome=${encodeURIComponent(nomeNovaKey)}`, {
        method: "POST",
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao gerar chave");
      setChaveGerada(data.api_key);
      setNomeNovaKey("");
      await carregar();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGerando(false);
    }
  };

  const revogarChave = async (keyId) => {
    try {
      const resp = await authService.fetchAuth(`${API}/keys/${keyId}`, { method: "DELETE" });
      if (!resp.ok) throw new Error("Erro ao revogar chave");
      toast.success("Chave revogada.");
      await carregar();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copiarChave = () => {
    navigator.clipboard.writeText(chaveGerada);
    toast.success("Chave copiada!");
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Code className="w-7 h-7 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold">Developer Portal</h1>
          <p className="text-sm text-muted-foreground">API Pública v1 - integre sistemas externos ao ClickHub</p>
        </div>
      </div>

      {/* Aviso da chave recém-gerada */}
      {chaveGerada && (
        <Card className="p-4 mb-6 bg-yellow-50 border-yellow-300">
          <p className="text-sm font-medium text-yellow-800 mb-2">
            ⚠️ Guarde esta chave agora - ela não será mostrada novamente:
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white p-2 rounded text-sm break-all">{chaveGerada}</code>
            <Button size="sm" onClick={copiarChave}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => setChaveGerada(null)}>
            Entendi, fechar
          </Button>
        </Card>
      )}

      {/* Gerar nova chave */}
      <Card className="p-4 mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <Key className="w-4 h-4" /> Gerar nova chave de API
        </h2>
        <div className="flex gap-2">
          <Input
            placeholder="Nome (ex: ERP da loja, integração parceiro X)"
            value={nomeNovaKey}
            onChange={(e) => setNomeNovaKey(e.target.value)}
          />
          <Button onClick={gerarChave} disabled={gerando}>
            {gerando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Gerar
          </Button>
        </div>
      </Card>

      {/* Lista de chaves */}
      <Card className="p-4 mb-6">
        <h2 className="font-semibold mb-3">Chaves ativas</h2>
        {carregando ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...
          </div>
        ) : keys.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">Nenhuma chave gerada ainda.</p>
        ) : (
          <div className="space-y-2">
            {keys.map((k) => (
              <div key={k.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-medium">{k.nome}</p>
                  <p className="text-xs text-muted-foreground font-mono">{k.key_preview}</p>
                  <p className="text-xs text-muted-foreground">Uso hoje: {k.contador_uso || 0} / 1000</p>
                </div>
                <Button size="sm" variant="outline" className="text-red-600" onClick={() => revogarChave(k.id)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Revogar
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Documentação resumida */}
      {docs && (
        <Card className="p-4">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <ExternalLink className="w-4 h-4" /> Endpoints disponíveis
          </h2>
          <p className="text-sm text-muted-foreground mb-3">{docs.autenticacao}</p>
          <p className="text-xs text-muted-foreground mb-3">{docs.rate_limit}</p>
          <div className="space-y-1">
            {docs.endpoints?.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">{e.metodo}</span>
                <code className="text-xs">{e.rota}</code>
                <span className="text-muted-foreground text-xs">- {e.descricao}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default DeveloperPortal;
